import { useState, useEffect } from "react";
import { Check, Loader2, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { useApp, applyTheme, applyCustomColors } from "../context/AppContext";
import { THEMES } from "../i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import ImageUpload from "./ImageUpload";

export default function DesignEditor() {
  const { refreshSettings, theme: activeTheme } = useApp();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto mt-10" />;

  const set = (path, value) => {
    setS((prev) => {
      const copy = structuredClone(prev);
      const keys = path.split(".");
      let o = copy;
      for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]];
      o[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  const previewTheme = (id) => { set("theme", id); applyTheme(id); if (s.custom_colors) applyCustomColors(s.custom_colors); };
  const custom = s.custom_colors || {};
  const setColor = (k, v) => { const nc = { ...(s.custom_colors || {}), [k]: v }; set("custom_colors", nc); applyCustomColors(nc); };
  const resetColors = () => { set("custom_colors", {}); applyTheme(s.theme); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        theme: s.theme, custom_colors: s.custom_colors || {}, logo_url: s.logo_url, hero_image_url: s.hero_image_url,
        hero: s.hero, brand_name_ar: s.brand_name_ar, brand_name_en: s.brand_name_en,
        sections_visible: s.sections_visible, why_us: s.why_us,
      });
      toast.success("تم حفظ ونشر التصميم");
      await refreshSettings();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const sections = { featured: "الخدمات المميزة", offers: "العروض", why_us: "لماذا نحن", reviews: "التقييمات", contact: "التواصل" };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">تصميم الموقع</h1>
        <Button onClick={save} disabled={saving} className="rounded-full font-semibold" data-testid="save-design-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : <Check className="h-4 w-4 me-1" />} حفظ ونشر
        </Button>
      </div>

      {/* Themes */}
      <section className="rounded-2xl bg-card cb-card-shadow p-5 mb-5">
        <h2 className="font-bold mb-3">مظهر الموقع (الثيم)</h2>
        <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> اضغطي على الثيم للمعاينة الفورية، ثم احفظي للنشر.</p>
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(THEMES).map(([id, th]) => (
            <button key={id} onClick={() => previewTheme(id)} data-testid={`theme-${id}`}
              className={`rounded-2xl border-2 p-3 text-start transition-all ${s.theme === id ? "border-primary ring-2 ring-primary/30" : "border-border"}`}>
              <div className="flex gap-1 mb-2">
                <span className="h-6 w-6 rounded-full" style={{ background: th.primary }} />
                <span className="h-6 w-6 rounded-full" style={{ background: th.accent }} />
                <span className="h-6 w-6 rounded-full border" style={{ background: th.background }} />
              </div>
              <div className="text-sm font-semibold">{th.name_ar}</div>
            </button>
          ))}
        </div>
      </section>

      {/* Custom colors */}
      <section className="rounded-2xl bg-card cb-card-shadow p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold">ألوان مخصصة (اختياري)</h2>
          <Button size="sm" variant="ghost" onClick={resetColors} data-testid="reset-colors-btn">إرجاع ألوان الثيم</Button>
        </div>
        <p className="text-xs text-muted-foreground mb-3">غيّري الألوان الأساسية لتناسب هويتكِ — تُطبّق فوراً للمعاينة.</p>
        <div className="grid grid-cols-3 gap-3">
          {[["primary", "اللون الأساسي"], ["accent", "اللون المميز"], ["background", "لون الخلفية"]].map(([k, label]) => (
            <div key={k}>
              <Label className="mb-1 block text-xs">{label}</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={custom[k] || "#3B7182"} onChange={(e) => setColor(k, e.target.value)} className="h-10 w-12 rounded-lg border border-border cursor-pointer p-0.5" data-testid={`color-${k}`} />
                <Input value={custom[k] || ""} onChange={(e) => setColor(k, e.target.value)} className="rounded-xl h-10 text-xs" dir="ltr" placeholder="#3B7182" />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Branding */}
      <section className="rounded-2xl bg-card cb-card-shadow p-5 mb-5 space-y-4">
        <h2 className="font-bold">الهوية</h2>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="mb-1 block text-xs">اسم العلامة (عربي)</Label><Input value={s.brand_name_ar} onChange={(e) => set("brand_name_ar", e.target.value)} className="rounded-xl h-11" data-testid="brand-ar" /></div>
          <div><Label className="mb-1 block text-xs">اسم العلامة (إنجليزي)</Label><Input value={s.brand_name_en} onChange={(e) => set("brand_name_en", e.target.value)} className="rounded-xl h-11" dir="ltr" /></div>
        </div>
        <ImageUpload value={s.logo_url} onChange={(v) => set("logo_url", v)} label="الشعار (اختياري)" />
        <ImageUpload value={s.hero_image_url} onChange={(v) => set("hero_image_url", v)} label="صورة الواجهة الرئيسية" />
      </section>

      {/* Hero content */}
      <section className="rounded-2xl bg-card cb-card-shadow p-5 mb-5 space-y-3">
        <h2 className="font-bold">محتوى الواجهة الرئيسية</h2>
        <div><Label className="mb-1 block text-xs">العنوان (عربي)</Label><Input value={s.hero.title_ar} onChange={(e) => set("hero.title_ar", e.target.value)} className="rounded-xl h-11" data-testid="hero-title-ar" /></div>
        <div><Label className="mb-1 block text-xs">الوصف (عربي)</Label><Textarea value={s.hero.subtitle_ar} onChange={(e) => set("hero.subtitle_ar", e.target.value)} className="rounded-xl" rows={2} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="mb-1 block text-xs">الشارة (عربي)</Label><Input value={s.hero.badge_ar} onChange={(e) => set("hero.badge_ar", e.target.value)} className="rounded-xl h-11" /></div>
          <div><Label className="mb-1 block text-xs">زر الحجز (عربي)</Label><Input value={s.hero.cta_ar} onChange={(e) => set("hero.cta_ar", e.target.value)} className="rounded-xl h-11" /></div>
        </div>
        <div><Label className="mb-1 block text-xs">العنوان (إنجليزي)</Label><Input value={s.hero.title_en} onChange={(e) => set("hero.title_en", e.target.value)} className="rounded-xl h-11" dir="ltr" /></div>
      </section>

      {/* Sections visibility */}
      <section className="rounded-2xl bg-card cb-card-shadow p-5 space-y-2">
        <h2 className="font-bold mb-2">إظهار / إخفاء أقسام الصفحة الرئيسية</h2>
        {Object.entries(sections).map(([key, label]) => (
          <label key={key} className="flex items-center justify-between py-1.5">
            <span className="text-sm">{label}</span>
            <Switch checked={s.sections_visible?.[key] !== false} onCheckedChange={(v) => set(`sections_visible.${key}`, v)} data-testid={`section-toggle-${key}`} />
          </label>
        ))}
      </section>
    </div>
  );
}
